import { Framebuffer2D, Regl, Texture2D } from "regl";
import * as ReglPerf from "./ReglPerf";
import { fullScreenTriangles } from "./WebGLUtil";

export interface PercentileLineProps {
  /** source density buffer (we currently only read the red channel) */
  density: Texture2D;

  /** output texture 1 x width. red channel contains the mean, green is the sum */
  percentileFB: Framebuffer2D;

  percentile: number;
}

export interface PercentileLineCmd {
  (props: PercentileLineProps): void;
}

interface CmdProps extends PercentileLineProps {
  width: number;
}

export function percentileLineCmd(regl: Regl, height: number): PercentileLineCmd {
  const cmd = regl({
    vert: `
        precision highp float;
        attribute vec2 position;
        varying float u;
            
        void main() {
          gl_Position = vec4(position, 1.0, 1.0); // position range (-1, -1)
          u = 0.5 * (position.x + 1.0);           // uv range (0, 1) 
        }`,
    frag: `
        precision highp float;
        varying float u; // texel coordinate in src texture 
        uniform sampler2D density;
        uniform float percentile;

        void main() { 
          float dv = 1.0 / ${height}.0;             // advance by one texel each time
          float vStart = dv / 2.0;                  // start on the center of the first texel
          float v = vStart;
          float sum = 0.0;
          for (int i = 0; i < ${height}; i++) {
            float density = texture2D(density, vec2(u, v)).r;
            sum += density;

            v += dv; 
          }

          float targetSum = sum * percentile;
          float percentileV = -1e99; // TODO this won't be picked up by fillStat
          v = vStart;
          sum = 0.0;
          for (int i = 0; i < ${height}; i++) {
            float density = texture2D(density, vec2(u, v)).r;
            sum += density;
            if (sum > targetSum) {
              percentileV = v;
              break;
            }

            v += dv; 
          }
          gl_FragColor = vec4(u, percentileV, 0.0, 1.0);
        }`,
    uniforms: {
      density: (_ctx: any, props: CmdProps) => props.density,
      percentile: (_ctx, props: CmdProps) => props.percentile,
    },
    attributes: {
      position: fullScreenTriangles,
    },
    framebuffer: (_ctx: any, props: CmdProps) => props.percentileFB,
    depth: { enable: false, mask: false },
    primitive: "triangles",
    count: 6,
  });

  ReglPerf.registerCmd(cmd, "percentileLine");

  return function draw(props: PercentileLineProps): void {
    cmd(props);
  };
}
